import React, { useState } from "react";
import {
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  message,
  Breadcrumb,
  Modal,
  Divider,
  Table,
  Row,
  Col,
  Space,
  TableProps,
} from "antd";
import moment from "moment";
import { fetchSearchAccRequest } from "../../../core/services/account-request.service";
import { AccRequest } from "../../../core/interfaces/AccRequest";
import {
  AccReqDetailContext,
  SearchResultsContextType,
} from "../../../context/account-req-detail";
import { useNavigate, useLocation } from "react-router-dom";
import { ReqSrchParameters } from "../../../core/interfaces/ReqSrchParameters";
import { Employee } from "../../../core/interfaces/Employee";
import { fetchEmpDetails } from "../../../core/services/employee-service";
import { SearchOutlined } from "@ant-design/icons";
import { appInsights } from "../../../core/services/insight.service";
import GlobalErrorMessage from "../../global-error/GlobalErrorMessage";

const { RangePicker } = DatePicker;
const { Option } = Select;

const SearchForRequests: React.FC = () => {
  const [form] = Form.useForm();
  const [showResults, setShowResults] = useState(false);
  const [searchResults, setSearchResults] = useState<AccRequest[]>([]);
  const [open, setOpen] = useState(false);
  const [username, setUserName] = useState("");
  const [approvedBy, setApprovedByState] = useState(""); // For Approved by
  const [waitingOn, setWaitingOnState] = useState(""); 
  const [modalInputValue, setModalInputValue] = useState("");
  const [empData, setEmpData] = useState<Employee[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRequesterName, setSelectedRequesterName] = useState<string>("");
  const [selectedApprovedBy, setSelectedApprovedBy] = useState<string>(""); // New state for the selected name
  const [selectedWaitingOn, setSelectedWaitingOn] = useState<string>(""); // New state for the selected name
  const [selectedField, setSelectedField] = useState("");
  const [apiError, setApiError] = useState(null);
  const navigate = useNavigate();

  const columns: TableProps<Employee>["columns"] = [
      {
        title: "S/N",
        dataIndex: "index",
        key: "index",
        render: (text: string, record: Employee, index: number) =>
          `${index + 1}.`,
        width: "15%",
      },
      {
        title: "Name",
        dataIndex: "fullName",
        key: "fullName",
        render: (text) => <a>{text}</a>,
        width: "20%",
      },
      {
        title: "User ID/Email ID",
        dataIndex: "emailAddress",
        key: "emailAddress",
        align: "center",
        width: "20%",
      },
      {
        title: "PhoneExt",
        dataIndex: "phoneExt",
        key: "phoneExt",
        width: "15%",
      },
  
      {
        title: "Department",
        dataIndex: "deptName",
        key: "deptName",
        width: "20%",
      },
      {
        title: "Location",
        dataIndex: "locationName",
        key: "locationName",
        width: "20%",
      },
    ];
  
  const handleSearch = async (values: any) => {
    const {
      requestNumberRange,
      requestDateRangeFrom,
      requestDateRangeTo,
      requester,
      requestType,
      approver,
      waitingOn,
    } = values;

    const params: ReqSrchParameters = {
      DateFrom: requestDateRangeFrom?.toString()??"",
      DateTo: requestDateRangeTo?.toString()??"",
      RequestNumberFrom: requestNumberRange ? requestNumberRange[0] : "",
      RequestNumberTo: requestNumberRange ? requestNumberRange[1] : "",
      srcRtype: requestType ,
      Requester: requester,
      Approver: approver,
      WaitingOn: waitingOn,
    };

    try {
  const response = await fetchSearchAccRequest(params);
  const data = response || []; // Ensure data is an array even if the response is null/undefined
  setSearchResults(data);

  // Always navigate to the search results page
  const contextValue: SearchResultsContextType = {
    accRequest: data,
    srcType: params.srcRtype,
  };
  navigate("/manage requests/search-results", { state: { contextValue } });

  if (Array.isArray(data) && data.length > 0) {
    setShowResults(true);
    message.success("Search completed successfully");
  } else {
    message.warning("No matching records found. Displaying empty results.");
  }
} catch (error) {
  setApiError(error);
  message.error("Error searching requests");
      appInsights.trackEvent({ name: "SearchRequestsFailed", properties: { error: error } });
    }

  };
  const SearchUser = async (text: string) => {
    setLoading(true);
    try {
      const query = text && text.trim() !== "" ? text : "";
      const response = await fetchEmpDetails(text);
      setEmpData(response);
      if (response.length === 0) {
        message.warning("No matching records found.");
      }
    } catch (error) {
      setApiError(error);
      message.error("Failed to fetch users");
      appInsights.trackEvent({ name: "FetchUsersFailed", properties: { error: error } });
    } finally {
      setLoading(false);
    }
  };
  const showModal = async (value: string, field: "requester" | "approvedBy" | "waitingOn") => {
    setModalInputValue(value); // Set the modal input value
    setSelectedField(field); // Track the field that triggered the modal
    setOpen(true); // Open the modal
  
    // Fetch employee data if input is not empty
    if (value && value.trim() !== "") {
      await SearchUser(value);
    } else {
      setEmpData([]);
    }
  };

  const handleOk = () => {
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleModalInputChange = (e: any) => {
    setModalInputValue(e);
  };

  const setRequester = (text: string, name:string) => {
    setUserName(text);
    setSelectedRequesterName(name);
    form.setFieldsValue({ requester: text });
    setOpen(false);
    setEmpData([]);
  };
  const setApprovedBy = (text: string, name:string) => {
    setApprovedByState(text);
    setSelectedApprovedBy(name);
    form.setFieldsValue({ approvedBy: text });
    setOpen(false);
    setEmpData([]);
  };
  const setWaitingOn = (text: string, name:string) => {
    setWaitingOnState(text);
    setSelectedWaitingOn(name);
    form.setFieldsValue({ waitingOn: text });
    setOpen(false);
    setEmpData([]);
  };
  const handleRetry = () => {
    setApiError(null);
  };
  return (
    <>
     {apiError ? (
          <GlobalErrorMessage 
          error={apiError}
          visible={!!apiError}
          onClose={() => setApiError(null)}
          onRetry={handleRetry}
        />
        ) : (
      <><Breadcrumb
            items={[
              {
                title: "manage requests",
              },
              {
                title: "search for requests",
              },
            ]} /><div className="div-container">
              <Row gutter={16}>
                <Col span={12} offset={1}>
                  <div>
                    <p>Note: You can click on for calendar</p>
                  </div>
                  <Form form={form} layout="vertical" initialValues={{ requestType: "1" }} onFinish={handleSearch}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          label="Request Number From"
                          name={["requestNumberRange", 0]}
                        >
                          <Input placeholder="From" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label="Request Number To"
                          name={["requestNumberRange", 1]}
                        >
                          <Input placeholder="To" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Request Date From" name="requestDateRangeFrom">
                          <DatePicker format="MM/DD/YYYY" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Request Date To" name="requestDateRangeTo">
                          <DatePicker format="MM/DD/YYYY" />
                        </Form.Item>
                      </Col>

                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Request Type" name="requestType">
                          <Select>
                            <Option value="1">ACCOUNT</Option>
                            <Option value="2">BUSINESS UNIT</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Requester" name="requester">
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <Input
                              value={username}
                              onChange={(e) => setUserName(e.target.value)}
                              style={{ width: 200 }} />
                            <a onClick={() => showModal(username, "requester")}>
                              <SearchOutlined />
                            </a>
                            {selectedRequesterName && (<span style={{ marginLeft: 10 }}> {selectedRequesterName} </span>)}
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Approved by" name="approver">
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <Input
                              value={approvedBy}
                              onChange={(e) => setApprovedByState(e.target.value)}
                              style={{ width: 200 }} />
                            <a onClick={() => showModal(approvedBy, "approvedBy")}>
                              <SearchOutlined />
                            </a>
                            {selectedApprovedBy && (<span style={{ marginLeft: 10 }}> {selectedApprovedBy} </span>)}
                          </div>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Waiting on" name="waitingOn">
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <Input
                              value={waitingOn}
                              onChange={(e) => setWaitingOnState(e.target.value)}
                              style={{ width: 200 }} />
                            <a onClick={() => showModal(waitingOn, "waitingOn")}>
                              <SearchOutlined />
                            </a>
                            {selectedWaitingOn && (<span style={{ marginLeft: 10 }}> {selectedWaitingOn} </span>)}
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item>
                      <Button type="primary" htmlType="submit">
                        Search
                      </Button>
                      <Button
                        type="default"
                        style={{ marginLeft: "10px" }}
                        onClick={() => window.history.back()}
                      >
                        Back
                      </Button>
                    </Form.Item>
                  </Form>
                </Col>
              </Row>
              <Modal
                title="Search For Employee"
                open={open}
                onOk={handleOk}
                onCancel={handleCancel}
                width={1000}
              >
                <Divider></Divider>
                <Space direction="vertical">
                  <Row gutter={[16, 32]}>
                    <Col span={16}>
                      <div>Enter Search Query</div>
                    </Col>
                  </Row>
                  <Row gutter={[16, 32]}>
                    <Col span={18}>
                      <div>
                        <Input
                          value={modalInputValue}
                          onChange={(e) => handleModalInputChange(e.target.value)} />
                      </div>
                    </Col>
                    <Col span={6}>
                      <div>
                        <Button
                          type="primary"
                          onClick={() => SearchUser(modalInputValue)}
                        >
                          Search
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Space>

                <Table
                  dataSource={empData}
                  columns={columns}
                  pagination={false}
                  scroll={{ y: 150 }}
                  loading={loading}
                  locale={{
                    emptyText: "No matching record found. Kindly refine your criteria!",
                  }}
                  onRow={(record) => ({
                    onClick: () => {
                      if (selectedField === "requester") {
                        setRequester(record.emailAddress, record.fullName); // Set Requester
                      } else if (selectedField === "approvedBy") {
                        setApprovedBy(record.emailAddress, record.fullName); // Set Approved by
                      } else if (selectedField === "waitingOn") {
                        setWaitingOn(record.emailAddress, record.fullName); // Set Waiting on
                      }
                      setOpen(false); // Close the modal
                    },
                  })}
                  style={{ marginTop: "20px" }}
                  size="small" />

                <Divider></Divider>
              </Modal>
            </div></>
    )}
    </>
  );
};

export default SearchForRequests;
